/**
 * Terms of Use and Privacy Policy.
 *
 * ⚠️ NOT LEGAL ADVICE, AND NOT A LAWYER'S WORK. These were written to describe
 * accurately what this application actually does with data — every technical
 * claim below is true of the current implementation (bcrypt cost 12, HMAC'd
 * tokens and IPs, no plaintext IP storage, append-only audit log, and so on),
 * and each was checked against the code rather than assumed.
 *
 * What they are NOT is a review by counsel for the jurisdictions you operate
 * in. Storing children's health information touches real regimes — HIPAA-
 * adjacent expectations in the US, COPPA where under-13s are involved, and the
 * GDPR's "special category" rules for health data in the EU/UK. Have a lawyer
 * read these before you rely on them, and fill in OPERATOR_* below with your
 * real legal entity and contact address.
 *
 * Keeping the two documents in one module on purpose: they cross-reference each
 * other, and a change to how data is handled should be a single edit here.
 */

/** ⚠️ Replace with your real operating entity and a monitored address. */
export const OPERATOR_NAME = "Leena's List";
export const OPERATOR_CONTACT = "privacy@leenaslist.abedubas.dev";

export const LEGAL_LAST_UPDATED = "2026-09-02";
/** The Privacy Policy alone changed on Oct 22 (pickup & consent no longer kept on a profile). */
export const PRIVACY_LAST_UPDATED = "2026-10-22";

export interface LegalSection {
  heading: string;
  body: string[];
}

export const TERMS_SECTIONS: LegalSection[] = [
  {
    heading: "1. What this service does",
    body: [
      `${OPERATOR_NAME} lets a parent or guardian keep one care profile for a child and share a scoped, revocable link with a caregiver. The caregiver needs no account — the link itself is the credential, so treat it like a key.`,
      "The service is offered as-is during beta. Features may change or be withdrawn, and you should not rely on it as your only record of your child's medical information. Keep whatever you would have kept anyway.",
    ],
  },
  {
    heading: "2. Not medical or emergency advice",
    body: [
      "This is an information-handoff tool, not a medical device, not a monitoring service, and not a substitute for professional judgement. In an emergency, contact emergency services first and use this second.",
      "We do not verify anything you enter. Accuracy is entirely yours, and out-of-date information can be worse than none.",
    ],
  },
  {
    heading: "3. Your account",
    body: [
      "You must be 18 or older and the parent or legal guardian of any child whose information you add, or otherwise authorised to share it.",
      "Keep your password to yourself. You are responsible for what happens under your account, and for every share link you create, including who you send it to and how long you leave it live.",
    ],
  },
  {
    heading: "4. Sharing, and what it means",
    body: [
      "A share link reveals exactly the sections you choose when you create it, and nothing else. You can revoke any link at any time, which stops it working immediately for everyone holding it.",
      "We cannot control what a recipient does with what they see — they can read it, remember it, screenshot it, or forward the link. Vet the people you share with. The service moves information; it does not vouch for anyone.",
    ],
  },
  {
    heading: "5. Acceptable use",
    body: [
      "Do not upload information about a child you are not responsible for, share links with people you have not authorised, attempt to reach profiles that are not yours, probe the service for vulnerabilities, or automate access to it.",
      "Accounts that do these things may be suspended without notice.",
    ],
  },
  {
    heading: "6. Availability and liability",
    body: [
      "The service is provided as-is and as-available, without warranties of any kind. We do not promise it will be uninterrupted or error-free.",
      "To the fullest extent permitted by law, we are not liable for indirect or consequential loss, nor for decisions made — or not made — on the basis of information shared through the service.",
    ],
  },
  {
    heading: "7. Ending it",
    body: [
      "You may stop using the service at any time and ask us to delete your account and its data; see the Privacy Policy for how.",
      "We may suspend or end access for breach of these terms, or if we stop offering the service.",
    ],
  },
  {
    heading: "8. Changes",
    body: [
      `These terms were last updated on ${LEGAL_LAST_UPDATED}. If they change materially you may be asked to review and accept them again.`,
    ],
  },
];

export const PRIVACY_SECTIONS: LegalSection[] = [
  {
    heading: "1. The short version",
    body: [
      "You enter information about your child so that a caregiver can read it when they need it. We store it, we let you share precisely the parts you choose, and we do not sell it or use it for advertising.",
      "This information is sensitive — it concerns a child's health — and it is handled accordingly. What follows describes what actually happens to it, not what typically happens to data.",
    ],
  },
  {
    heading: "2. What we hold",
    body: [
      "Account: your name, email address, and a bcrypt hash of your password. We never store your password itself and cannot recover it — that is why a forgotten password must be reset rather than retrieved.",
      "Child profile: whatever you enter — a nickname, and if you choose to add it, a legal name; age, grade, school and teacher details, emergency contacts, allergies and emergency plans, medications, routines, feeding notes, medical history (including any insurance notes you add there) and immunisations. All of it is optional except the nickname. A legal name is never shown on a care link unless you tick \"Include legal name\" for that link. We do not accept children's photos; a photo saved before that change is no longer shown to anyone, and you can delete it from the child's profile.",
      "We no longer collect authorised pickup contacts or the consent-to-treat record (consent, insurance carrier, policy and group numbers, preferred hospital). Any you saved before that change are kept, but they are no longer shown to anyone or shared on any care link.",
      "Usage: when a share link is opened, we append a record to a log that cannot be edited or deleted. That record stores the visitor's IP address and browser only as one-way HMAC hashes — never in readable form — which is enough to notice abuse from one source and not enough to identify a visitor.",
    ],
  },
  {
    heading: "3. Who else sees it",
    body: [
      "Caregivers you choose, and only the sections you selected for their link. Sections you did not share are not merely hidden in the page — they are never sent to the caregiver's device at all.",
      "Service providers who make the product work: our hosting provider, and an email provider for notifications and password resets. They process data on our instructions.",
      "Nobody else. We do not sell your data, we do not share it with advertisers or data brokers, and we do not use your child's information to train anything.",
    ],
  },
  {
    heading: "4. How it is protected",
    body: [
      "Share links use long random tokens, and only a keyed hash of each token is stored — the link itself exists only where you sent it, and cannot be recovered from our database. Rotating our signing key invalidates every outstanding link at once.",
      "Traffic is encrypted in transit. Passwords are hashed with bcrypt. Session cookies are HTTP-only, and two-factor authentication is available on your account.",
      "No system is perfectly secure, and we will not pretend otherwise. If a breach affects you, we will tell you.",
    ],
  },
  {
    heading: "5. How long it is kept",
    body: [
      "Profile information is kept until you delete it or close your account. Deleting a child profile removes it from the app immediately.",
      "Access-log entries are retained as a security record and are deliberately append-only, so they survive the deletion of the link they describe. They contain no readable personal data.",
      "Backups are retained for 30 days and then deleted automatically.",
    ],
  },
  {
    heading: "6. Your choices",
    body: [
      "You can see and edit everything on your child's profile at any time, revoke any share link immediately, and ask us to delete your account and its contents.",
      "Depending on where you live you may also have rights to a copy of your data, to correct it, or to object to how it is handled.",
      `To exercise any of these, contact ${OPERATOR_CONTACT}.`,
    ],
  },
  {
    heading: "7. Children",
    body: [
      "This service is for parents and guardians, not for children. Accounts are for adults, and children do not sign in or interact with it.",
      "Information about a child is provided by the adult responsible for them. If you believe a child's information has been added by someone without the right to do so, contact us and we will remove it.",
    ],
  },
  {
    heading: "8. Changes and contact",
    body: [
      `This policy was last updated on ${PRIVACY_LAST_UPDATED}.`,
      `Questions, requests, or concerns: ${OPERATOR_CONTACT}.`,
    ],
  },
];
